// @givmo/app-charity — how the "Name on Givmo" card reads the profile's `display_name` block
// (CPA-BE44, Charity Portal design 20 §5, §7). Tolerant: an unknown state reads as `none`, an unknown
// reason as a general sentence, and nothing here throws.
//
// WHICH REVIEWER DECISION THE CARD SHOWS. The backend serves `last_decision` only while it is the
// newest event in the charity's display-name history: a request, a go-live or an end after it, and
// it is null (CPA-BE64). So the card shows a rejection as served, naming the name decided; an
// approval never shows, since an approved name is the live name the card already shows, with when it
// went live (`live_since`, CPA-BE72).

import type { CharityDisplayNameStateOut } from '@givmo/api-client';

export type DisplayNameState = 'none' | 'live' | 'pending_review';

export function displayNameState(block: CharityDisplayNameStateOut): DisplayNameState {
  return block.state === 'live' || block.state === 'pending_review' ? block.state : 'none';
}

/**
 * Why a charity cannot set a display name now, as the PUT's 409 says it
 * (`charity_display_name_service._NOT_AVAILABLE_SENTENCES`), keyed by the block's
 * `ineligible_reason`.
 */
const INELIGIBLE_SENTENCES: Readonly<Record<string, string>> = {
  terms_not_accepted:
    'Your organization needs to accept the Charity Terms before it can choose a display name.',
  claim_not_verified:
    'Your organization can choose a display name once its claim has been approved.',
  no_irs_legal_name:
    'We can’t offer a display name yet because the IRS legal name for your EIN isn’t on file. Please contact support@givmocharitable.org.',
  not_active: 'A display name isn’t available while your organization isn’t listed on Givmo.',
};

export const INELIGIBLE_FALLBACK =
  'A display name isn’t available for your organization right now.';

export function ineligibleSentence(reason: string | null | undefined): string {
  return (reason && INELIGIBLE_SENTENCES[reason]) || INELIGIBLE_FALLBACK;
}

/** One piece of a reason's sentence: words, words that lead to a console page, or an address. */
export type SentencePart =
  | { kind: 'text'; text: string }
  | { kind: 'route'; text: string; to: string }
  | { kind: 'mail'; text: string; address: string };

type LinkPart = Exclude<SentencePart, { kind: 'text' }>;

/** Where a charity writes to Givmo about its listing or its IRS record (the support address). */
export const SUPPORT_EMAIL = 'support@givmocharitable.org';
const SUPPORT: LinkPart = { kind: 'mail', text: SUPPORT_EMAIL, address: SUPPORT_EMAIL };

/**
 * The words that lead somewhere: the Terms page, where the Donations "Finish setting up" strip's
 * Terms step goes (`getting-started-strip.tsx`), and the support address, as a `mailto:` link.
 */
const INELIGIBLE_LINKS: Readonly<Record<string, readonly LinkPart[]>> = {
  terms_not_accepted: [{ kind: 'route', text: 'accept the Charity Terms', to: '/terms' }],
  no_irs_legal_name: [SUPPORT],
};

/** `sentence` split at each link's words, in order; a link whose words it lacks stays words. */
function withLinks(sentence: string, links: readonly LinkPart[]): SentencePart[] {
  const parts: SentencePart[] = [];
  let rest = sentence;
  for (const link of links) {
    const at = rest.indexOf(link.text);
    if (at < 0) continue;
    if (at > 0) parts.push({ kind: 'text', text: rest.slice(0, at) });
    parts.push(link);
    rest = rest.slice(at + link.text.length);
  }
  if (rest.length > 0) parts.push({ kind: 'text', text: rest });
  return parts;
}

/** The reason's sentence (the backend's own), with the words that lead somewhere as links. */
export function ineligibleParts(reason: string | null | undefined): SentencePart[] {
  return withLinks(ineligibleSentence(reason), (reason && INELIGIBLE_LINKS[reason]) || []);
}

/**
 * Beside a LIVE display name the backend's sentences, written for a charity with none ("We can’t
 * offer a display name yet …"), read as if the live name were gone. So the card says each reason
 * one way: why it can't choose a new display name, the next step, and how to switch the live one off.
 * Only `not_active` (the weekly catalog sync) and `no_irs_legal_name` (the IRS reference refresh)
 * reach a live name in the normal course; `terms_not_accepted` only by an operator's hand edit or
 * CPA-BE70's open bug, `claim_not_verified` by no app path. Each has its words all the same.
 */
const INELIGIBLE_LIVE_SENTENCES: Readonly<Record<string, string>> = {
  not_active: `While your organization isn’t listed on Givmo, you can’t choose a new display name. Questions? Contact ${SUPPORT_EMAIL}.`,
  no_irs_legal_name: `The IRS no longer lists a legal name for your EIN, so you can’t choose a new display name. Please contact ${SUPPORT_EMAIL}.`,
  terms_not_accepted:
    'Until your organization accepts the Charity Terms, you can’t choose a new display name. Review and accept the Charity Terms.',
  claim_not_verified:
    'Until your organization’s claim is approved, you can’t choose a new display name. Our team is reviewing it.',
};

const INELIGIBLE_LIVE_FALLBACK = 'You can’t choose a new display name right now.';

const INELIGIBLE_LIVE_LINKS: Readonly<Record<string, readonly LinkPart[]>> = {
  not_active: [SUPPORT],
  no_irs_legal_name: [SUPPORT],
  terms_not_accepted: [
    { kind: 'route', text: 'Review and accept the Charity Terms', to: '/terms' },
  ],
};

/** Beside a live display name: the reason's live-name words, links included (the way off apart). */
export function ineligibleLiveParts(reason: string | null | undefined): SentencePart[] {
  const sentence = (reason && INELIGIBLE_LIVE_SENTENCES[reason]) || INELIGIBLE_LIVE_FALLBACK;
  return withLinks(sentence, (reason && INELIGIBLE_LIVE_LINKS[reason]) || []);
}

/**
 * The label over the legal name. While a display name is live and the IRS row is gone, the block
 * serves the name the IRS listed when the display name went live (the live row's snapshot), so the
 * label says so; everywhere else it is the IRS's current record.
 */
export function legalNameLabel(block: CharityDisplayNameStateOut): string {
  return displayNameState(block) === 'live' && block.ineligible_reason === 'no_irs_legal_name'
    ? 'Legal name (last IRS record)'
    : 'Legal name (from the IRS)';
}

/** How to switch a live display name off while the charity can't choose a new one. */
export function switchOffSentence(displayName: string, catalogName: string): string {
  return `To switch “${displayName}” off, type “${catalogName}” above and save.`;
}

/** A name as the backend stores and compares it: NFC, trimmed, its spaces collapsed, capitals kept. */
function storedName(value: string | null | undefined): string {
  return (value ?? '').normalize('NFC').trim().split(/\s+/u).join(' ');
}

/**
 * Whether a typed name is the catalog name, as the backend compares names (spacing aside, capitals
 * kept). One rule (D-CP-109): beside a live display name the card sends it and the backend ends the
 * display name ("Asking for the catalog name back is ending the display name"); with none live it
 * would change nothing, so the card says so instead of sending it. Any other name goes to the check,
 * the IRS legal name in capitals and a restyle in the charity's own capitals included ("CASA of
 * Travis County" for the catalog's "Casa of Travis County"), which design 20 §10 lets go live.
 */
export function isCatalogName(typed: string, catalogName: string | null | undefined): boolean {
  const name = storedName(typed);
  return name.length > 0 && name === storedName(catalogName);
}

function isoTime(value: string | null | undefined): number | null {
  if (!value) return null;
  const time = Date.parse(value);
  return Number.isNaN(time) ? null : time;
}

export interface ShownDecision {
  /** The name the reviewer decided; null from a backend that does not say (before CPA-BE64). */
  name: string | null;
  /** The reviewer's note, which the charity reads; null when the reviewer left none. */
  note: string | null;
  decidedAt: string;
}

/** The reviewer decision the card shows, by the rule in this file's header; null for none. */
export function decisionToShow(block: CharityDisplayNameStateOut): ShownDecision | null {
  const decision = block.last_decision;
  if (!decision || decision.outcome !== 'rejected') return null;
  if (isoTime(decision.decided_at) === null) return null;
  return {
    name: decision.name?.trim() || null,
    note: decision.note?.trim() || null,
    decidedAt: decision.decided_at,
  };
}

/** The EIN as `XX-XXXXXXX`; a value that is not nine digits is shown as given. */
export function formatEin(value: string | null | undefined): string | null {
  const raw = value?.trim() ?? '';
  if (raw.length === 0) return null;
  const digits = raw.replace(/\D/g, '');
  return digits.length === 9 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : raw;
}

/**
 * A server instant's day, e.g. "Oct 21, 2026" (UTC, so it reads the same everywhere): a decision's
 * day, and the day a display name went live.
 */
export function formatDay(value: string | null | undefined): string | null {
  const time = isoTime(value);
  if (time === null) return null;
  return new Date(time).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}
